import { Component, OnInit, ViewChild } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import { switchMap } from 'rxjs';

import { PermissionService } from '../../../../../core/services/permission.service';
import { environment } from '../../../../../environments/environment';
import { LocationService } from '../../../../../modules/warehouse-management/features/locations/services/location-api.service';
import { WarehouseModel } from '../../../../../modules/warehouse-management/features/warehouses/models/warehouse.models';
import { WarehouseService } from '../../../../../modules/warehouse-management/features/warehouses/services/warehouse-api.service';
import { Apps, Status } from '../../../../enums/enum';
import { LoginModel } from '../../../../models/baseUser.model';
import { MenuItem } from '../../../../models/menuItems.model';
import { ErrorHandlerService } from '../../../../services/error-handler.service';
import { SharedUserApiService } from '../../../../services/shared-user-api.service';
import { UserService } from '../../../../services/user.service';

@Component({
  selector: 'app-navigation-menu',
  standalone: false,
  templateUrl: './navigation-mobile.component.html',
  styleUrl: './navigation-mobile.component.css'
})
export class NavigationMobileComponent implements OnInit {
  @ViewChild('menubar') menubar: any;
  @ViewChild('switchWarehouseModal') switchWarehouseModal: any;
  imsAppId = Apps.IMS;
  listerAppId = Apps.Lister;
  menuIsActive = false;
  selectedMenu: string | null = null;
  userDetails: LoginModel = new LoginModel();
  defaultWarehouseName: string = '';
  currentUserRoles: any[] = [];
  userPermissions: string[] = [];
  isDefault: boolean = true;
  selectedWarehouseId: number = 0;
  selectedWarehouseName: string = '';
  warehouses: WarehouseModel[] = [];
  isWeb: boolean = true;
  listerUrl: string = environment.listerUrl;
  menuItems: MenuItem[] = [
    {
      title: 'Dashboard',
      route: '/dashboard',
      active: true,
      activeIcon: 'assets/images/icons/dashboard-active.svg',
      inactiveIcon: 'assets/images/icons/dashboard-inactive.svg'
    },
    {
      title: 'Inquiry',
      active: false,
      route: 'inquiries',
      activeIcon: 'assets/images/icons/enquiry-active.svg',
      inactiveIcon: 'assets/images/icons/enquiry-inactive.svg',
      requiresAnyPermission: true,
      requiredPermissions: ['Inquiry:ItemInquiry', 'Inquiry:LicensePlateInquiry', 'Inquiry:LocationInquiry']
    },
    {
      title: 'Product List',
      active: false,
      activeIcon: 'assets/images/icons/product-active.svg',
      inactiveIcon: 'assets/images/icons/product-inactive.svg',
      route: `${this.listerUrl}/product-list-mobile`
    },
    {
      title: 'Work',
      active: false,
      route: '/work-items',
      activeIcon: 'assets/images/icons/work-active.svg',
      inactiveIcon: 'assets/images/icons/work-inactive.svg'
    },
    {
      title: 'Quick Relocation',
      active: false,
      route: 'quick-relocation/relocation-process',
      activeIcon: 'assets/images/icons/inventory-active.svg',
      inactiveIcon: 'assets/images/icons/inventory-inactive.svg'
    },
    {
      title: 'Quick Transfer',
      active: false,
      route: 'quick-transfer-order/quick-transfer-process',
      activeIcon: 'assets/images/icons/quick-transfer-active.png',
      inactiveIcon: 'assets/images/icons/quick-transfer-inactive.png',
      requiredPermissions: ['TransferOrder:Create']
    },
    {
      title: 'Quick Receive',
      active: false,
      route: 'transfer/transfer-receive-orders-listing',
      routes: ['transfer/transfer-receive-orders-listing', 'transfer/quick-transfer-receive'],
      queryParams: { tab: 'readyForReceive' },
      activeIcon: 'assets/images/icons/quick-receive-active.png',
      inactiveIcon: 'assets/images/icons/quick-receive-inactive.png',
      requiredPermissions: ['TransferOrder:View']
    },
    {
      title: 'Logout',
      active: false,
      route: '',
      activeIcon: 'assets/images/icons/logout-active.svg',
      inactiveIcon: 'assets/images/icons/logout-inactive.svg'
    }
  ];

  constructor(
    private router: Router,
    private userService: UserService,
    private sharedUserApiService: SharedUserApiService,
    private warehouseService: WarehouseService,
    private permissionService: PermissionService,
    private readonly warehouseLocationApiService: LocationService,
    private errorService: ErrorHandlerService
  ) {
    this.router = router;
    this.userDetails = this.userService.getUserFromLocalStorage();
    this.router.events.subscribe(e => e instanceof NavigationEnd && this.updateActiveMenuByUrl());
  }

  ngOnInit() {
    if (window.innerWidth <= 768) {
      this.getDefaultWarehouseName();
      this.isWeb = false;
      this.getCurrentUserRole();
      this.userPermissions = this.permissionService.getAllPermissions();
      this.filterMenuItems();
      this.getWarehouses();
      this.setWarehouse();
      this.updateActiveMenuByUrl();
    }
  }

  updateActiveMenuByUrl(): void {
    const active = this.menuItems.find(
      m => (m.route && this.router.url.includes(m.route)) || m.routes?.some(r => this.router.url.includes(r))
    );
    if (active) {
      this.menuItems.forEach(m => (m.active = m === active));
    }
  }

  onMenuClick(title: string): void {
    const selected = this.menuItems.find(menu => menu.title === title);
    if (selected) {
      if (selected.title === 'Logout') {
        this.logout();
        return;
      }
      this.menuItems.forEach(menu => {
        menu.active = menu.title === title;
      });
      if (selected.children && selected.children.length > 0) {
        this.selectedMenu = title;
      } else {
        this.selectedMenu = '';
        this.router.navigate([selected.route], { queryParams: selected.queryParams });
      }
    }
  }

  logout() {
    this.userService.logout();
    this.router.navigate(['/login']);
  }

  productListClick() {
    if (this.userDetails.imsDefaultWarehouseId === this.userDetails.defaultWarehouseId) {
      window.open(`${this.listerUrl}/product-list-mobile`, '_blank', 'noopener,noreferrer');
      return;
    }

    this.warehouseLocationApiService
      .getAllWarehouseLocationsLite({
        companyId: this.userDetails.companyId,
        warehouseId: this.userDetails.imsDefaultWarehouseId
      })
      .pipe(
        switchMap((locations: any) => {
          const firstLocationId = locations[0]?.id;
          if (!firstLocationId) throw new Error('No warehouse locations found.');
          return this.sharedUserApiService.setDefaultWarehouse(
            this.userDetails.userId,
            this.userDetails.imsDefaultWarehouseId,
            Apps.Lister,
            true,
            firstLocationId
          );
        })
      )
      .subscribe({
        next: () => {
          window.open(`${this.listerUrl}/product-list-mobile`, '_blank', 'noopener,noreferrer');
        },
        error: err => {
          this.errorService.handleApiError(err);
        }
      });
  }

  getDefaultWarehouseName(): any {
    this.warehouseService.getWarehouseById(this.userDetails.imsDefaultWarehouseId!).subscribe({
      next: warehouse => {
        this.defaultWarehouseName = warehouse.name;
      }
    });
  }
  async getCurrentUserRole(): Promise<void> {
    try {
      const roles = await this.userService.getUserRolesandPermission();
    } catch (error) {
      console.error('Error loading user roles:', error);
    }
  }

  hideMenuForApp() {
    if (!this.userService.hasAppAccess('Lister')) {
      this.menuItems.splice(2, 1);
    }
    //add  more if block for app specific menues
  }
  filterMenuItems() {
    this.hideMenuForApp();
    this.menuItems = this.filterMenuItemsByPermissions(this.menuItems);
  }

  setWarehouse() {
    const storedPref = localStorage.getItem('imsSavePreference');
    if (storedPref !== null) {
      this.isDefault = storedPref === 'true';
    }
    if (!this.isDefault) {
      const storedSelected = localStorage.getItem('imsSelectedWarehouseId');
      this.selectedWarehouseId = storedSelected ? Number(storedSelected) : this.userDetails.imsDefaultWarehouseId;
    } else {
      this.selectedWarehouseId = this.userDetails.imsDefaultWarehouseId;
    }
  }

  openWarehouseSelectorModal(): void {
    // Close offcanvas
    const offcanvasElement = this.menubar?.nativeElement || document.getElementById('menubar');
    if (offcanvasElement) {
      const offcanvas = (window as any).bootstrap.Offcanvas.getInstance(offcanvasElement);
      if (offcanvas) {
        offcanvas.hide();
      }
    }

    // Open modal
    setTimeout(() => {
      const modalElement = this.switchWarehouseModal?.nativeElement || document.getElementById('SwitchWarehouseModal');
      if (modalElement) {
        const modal = new (window as any).bootstrap.Modal(modalElement);
        modal.show();
      }
    }, 300);
  }

  getWarehouses() {
    const queryParams: Record<string, any> = {
      CompanyId: this.userDetails.companyId,
      Status: Status.Active
    };
    this.warehouseService.getAllWarehouses(queryParams).subscribe(
      response => {
        this.warehouses = response as WarehouseModel[];
        this.selectedWarehouseName = this.warehouses.find(item => item.id === this.selectedWarehouseId)?.name ?? '';
      },
      error => {
        this.errorService.handleApiError(error);
      }
    );
  }

  toggleDefaultPreferrence() {
    this.isDefault = !this.isDefault;
    localStorage.setItem('imsSavePreference', String(this.isDefault));
  }

  SaveWarehouse() {
    localStorage.setItem('imsSavePreference', String(this.isDefault));
    localStorage.setItem('imsSelectedWarehouseId', String(this.selectedWarehouseId));

    if (this.isDefault) {
      this.sharedUserApiService
        .setDefaultWarehouse(this.userDetails.userId, this.selectedWarehouseId, Apps.IMS, this.isDefault)
        .subscribe(
          result => {
            this.updateLocalStorageUserAndReload();
          },
          error => {
            this.errorService.handleApiError(error);
          }
        );
    } else {
      this.updateLocalStorageUserAndReload();
    }
  }

  private updateLocalStorageUserAndReload() {
    this.syncSessionWarehouse(this.selectedWarehouseId);

    if (this.isDefault) {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        try {
          const loggedInUser = JSON.parse(userStr);
          loggedInUser.imsDefaultWarehouseId = this.selectedWarehouseId;
          localStorage.setItem('user', JSON.stringify(loggedInUser));
        } catch (e) {
          console.error('Error updating permanent user storage:', e);
        }
      }
    }

    this.selectedWarehouseName = this.warehouses.find(item => item.id === this.selectedWarehouseId)?.name ?? '';

    const currentUrl = this.router.url;
    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this.router.navigate([currentUrl]);
      window.location.reload();
    });
  }

  private syncSessionWarehouse(warehouseId: number) {
    this.userDetails.imsDefaultWarehouseId = warehouseId;
  }

  private filterMenuItemsByPermissions(menuItems: MenuItem[]): MenuItem[] {
    return menuItems.filter(menu => {
      if (!menu.requiredPermissions || menu.requiredPermissions.length === 0) {
        if (menu.children) {
          menu.children = this.filterMenuItemsByPermissions(menu.children);
          return menu.children.length > 0;
        }
        return true;
      }

      const hasPermission = menu.requiresAnyPermission
        ? menu.requiredPermissions.some(permission => this.userPermissions.includes(permission))
        : menu.requiredPermissions.every(permission => this.userPermissions.includes(permission));

      if (hasPermission && menu.children) {
        menu.children = this.filterMenuItemsByPermissions(menu.children);
        return menu.children.length > 0;
      }

      return hasPermission;
    });
  }
}
